import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import type { Except } from "type-fest";
import { z } from "zod";
import type { ProviderId} from "../provider-id";
import { providerIdZodSchema } from "../provider-id";
import type {
    ProviderIntegrationId} from "./provider-integration-id";
import {
    providerIntegrationIdFactory,
    providerIntegrationIdZodSchema,
} from "./provider-integration-id";

type _IntegrationPartner = "PARAKEY" | "ZETTLE";
type _IntegrationType = "ACCESS" | "CHECKOUT";

type ParakeyIntegrationData = {
    readonly accessKey: string;
    readonly domainId: string;
};

type ZettleIntegrationData = {
    readonly accessKey: string;
    readonly clientId: string;
};

type LockyIntegrationData = {
    //"http://server2.thundertech.no:20001"
    //"https://server1.thundertech.no:20001"
    readonly serverUrl: string;
    readonly jwtToken: string;
};

type ProviderIntegration =
    | ProviderIntegrationParakey
    | ProviderIntegrationZettle
    | ProviderIntegrationLocky
    | ProviderIntegrationChannex;

type ProviderIntegrationParakey = {
    readonly _id: ProviderIntegrationId;
    readonly _type: "PROVIDER_INTEGRATION";
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly integrationPartner: "PARAKEY";
    readonly integrationType: "ACCESS";

    readonly ownerId: ProviderId;
    readonly integrationData: ParakeyIntegrationData;
};
type ProviderIntegrationZettle = {
    readonly _id: ProviderIntegrationId;
    readonly _type: "PROVIDER_INTEGRATION";
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly integrationPartner: "ZETTLE";
    readonly integrationType: "CHECKOUT";

    readonly ownerId: ProviderId;
    readonly integrationData: ZettleIntegrationData;
};

type ProviderIntegrationLocky = {
    readonly _id: ProviderIntegrationId;
    readonly _type: "PROVIDER_INTEGRATION";
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly integrationPartner: "LOCKY";
    readonly integrationType: "ACCESS";

    readonly ownerId: ProviderId;
    readonly integrationData: LockyIntegrationData;
};

type FactoryParams = Except<
    ProviderIntegrationParakey,
    "_id" | "_type" | "_createdAt"
>;
const parakeyIntegrationFactory = (params: FactoryParams) => {
    const command: ProviderIntegrationParakey = {
        ...params,
        _id: providerIntegrationIdFactory(),
        _type: "PROVIDER_INTEGRATION",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const providerIntegrationZodSchema: z.ZodSchema<ProviderIntegrationParakey> =
    z.object({
        _id: providerIntegrationIdZodSchema,
        _type: z.literal("PROVIDER_INTEGRATION"),
        _createdAt: isoAndUnixZodSchema,
        integrationPartner: z.literal("PARAKEY"),
        integrationType: z.literal("ACCESS"),
        ownerId: providerIdZodSchema,
        integrationData: z.object({
            accessKey: z.string(),
            domainId: z.string(),
        }),
    });

type FactoryParams2 = Except<
    ProviderIntegrationZettle,
    "_id" | "_type" | "_createdAt"
>;
const zettleIntegrationFactory = (params: FactoryParams2) => {
    const command: ProviderIntegrationZettle = {
        ...params,
        _id: providerIntegrationIdFactory(),
        _type: "PROVIDER_INTEGRATION",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const providerIntegrationZettleZodSchema: z.ZodSchema<ProviderIntegrationZettle> =
    z.object({
        _id: providerIntegrationIdZodSchema,
        _type: z.literal("PROVIDER_INTEGRATION"),
        _createdAt: isoAndUnixZodSchema,
        integrationPartner: z.literal("ZETTLE"),
        integrationType: z.literal("CHECKOUT"),
        ownerId: providerIdZodSchema,
        integrationData: z.object({
            accessKey: z.string(),
            clientId: z.string(),
        }),
    });

const providerIntegrationLockyZodSchema: z.ZodSchema<ProviderIntegrationLocky> =
    z.object({
        _id: providerIntegrationIdZodSchema,
        _type: z.literal("PROVIDER_INTEGRATION"),
        _createdAt: isoAndUnixZodSchema,
        integrationPartner: z.literal("LOCKY"),
        integrationType: z.literal("ACCESS"),
        ownerId: providerIdZodSchema,
        integrationData: z.object({
            serverUrl: z.string(),
            jwtToken: z.string(),
        }),
    });
type FactoryParamsLocky = Except<
    ProviderIntegrationLocky,
    "_id" | "_type" | "_createdAt"
>;
const lockyIntegrationFactory = (params: FactoryParamsLocky) => {
    const command: ProviderIntegrationLocky = {
        ...params,
        _id: providerIntegrationIdFactory(),
        _type: "PROVIDER_INTEGRATION",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

type ProviderIntegrationChannex = {
    readonly _id: ProviderIntegrationId;
    readonly _type: "PROVIDER_INTEGRATION";
    readonly _createdAt: IsoAndUnixTimestamp;

    readonly integrationPartner: "CHANNEX";
    readonly integrationType: "CHANNEL_MANAGER";

    readonly ownerId: ProviderId;
    readonly integrationData: {
        readonly propertyId: string;
    };
};

const providerIntegrationChannexZodSchema: z.ZodSchema<ProviderIntegrationChannex> =
    z.object({
        _id: providerIntegrationIdZodSchema,
        _type: z.literal("PROVIDER_INTEGRATION"),
        _createdAt: isoAndUnixZodSchema,
        integrationPartner: z.literal("CHANNEX"),
        integrationType: z.literal("CHANNEL_MANAGER"),
        ownerId: providerIdZodSchema,
        integrationData: z.object({
            propertyId: z.string(),
        }),
    });

type FactoryParamsChannex = Except<
    ProviderIntegrationChannex,
    "_id" | "_type" | "_createdAt"
>;
const channexIntegrationFactory = (params: FactoryParamsChannex) => {
    const command: ProviderIntegrationChannex = {
        ...params,
        _id: providerIntegrationIdFactory(),
        _type: "PROVIDER_INTEGRATION",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const isChannexIntegration = (
    integration: ProviderIntegration,
): integration is ProviderIntegrationChannex => {
    return integration.integrationPartner === "CHANNEX";
};

export {
    channexIntegrationFactory,
    isChannexIntegration,
    lockyIntegrationFactory,
    parakeyIntegrationFactory,
    providerIntegrationChannexZodSchema,
    providerIntegrationLockyZodSchema,
    providerIntegrationZettleZodSchema,
    providerIntegrationZodSchema,
    zettleIntegrationFactory,
};
export type {
    ProviderIntegration,
    ProviderIntegrationChannex,
    ProviderIntegrationLocky,
    ProviderIntegrationParakey,
    ProviderIntegrationZettle,
};
